import MutateEVSEAndConnectorsForm, {
  MutateEVSEAndConnectorsFormValues,
} from '../../ui/places/MutateEVSEAndConnectorsForm.tsx';
import {
  CreateEVSERequest,
  UpdateEVSEMetadataRequest,
} from '../../../api/requests/evse-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useState } from 'react';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import useUpdateEVSEMetadata from '../../../hooks/evses/useUpdateEVSEMetadata.ts';
import useCreateEVSE from '../../../hooks/evses/useCreateEVSE.ts';
import { Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';

interface AddEVSEAndConnectorsModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

export function AddEVSEAndConnectorsModal({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: AddEVSEAndConnectorsModalProps) {
  const [evseId, setEvseId] = useState<string | null>(null);
  const [isEVSENewlyCreatedInThisProcess, setIsEVSENewlyCreatedInThisProcess] =
    useState(false);

  const { mutateAsync: updateEVSEMetadata } = useUpdateEVSEMetadata();
  const { mutateAsync: createEVSE } = useCreateEVSE();

  const handleEvseCreated = (newEvseId: string) => {
    setEvseId(newEvseId);
    setIsEVSENewlyCreatedInThisProcess(true);
  };

  const { mutate: handleSubmit, isPending: isUpdateEVSEMetadataPending } =
    useMutation<void, ErrorResponse, MutateEVSEAndConnectorsFormValues>({
      mutationFn: async (formValues) => {
        if (formValues.evseId && formValues.isEVSEAlreadyCreatedBefore) {
          const updateEVSERequest: UpdateEVSEMetadataRequest = {
            evseId: formValues.evseId,
            floorLevels: formValues.evseFloorLevel,
            evseSerialNumber: formValues.evseSerialNumber,
          };
          await updateEVSEMetadata(updateEVSERequest);
        } else if (
          !formValues.evseId &&
          !formValues.isEVSEAlreadyCreatedBefore
        ) {
          const createEVSERequest: CreateEVSERequest = {
            chargingServiceId: serviceId,
            evseSerialNumber: formValues.evseSerialNumber,
            floorLevels: formValues.evseFloorLevel,
            status: 'OPERATIONAL',
          };
          await createEVSE(createEVSERequest);
        }
      },
      onSuccess: (_, variables) => {
        if (variables.evseId && variables.isEVSEAlreadyCreatedBefore) {
          notifySuccess('更新充電資訊成功');
        } else {
          notifySuccess('新增充電資訊成功');
        }
        onSuccess?.();
      },
      onError: (error) => {
        notifyError('新增充電資訊失敗', error.message);
      },
    });

  const handleClose = () => {
    setEvseId(null);
    setIsEVSENewlyCreatedInThisProcess(false);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={false}
      title={'新增充電資訊'}
      centered
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <MutateEVSEAndConnectorsForm
        initialValues={{
          serviceId: serviceId,
          isEVSEAlreadyCreatedBefore: false,
          isEVSENewlyCreatedDuringThisProcess: isEVSENewlyCreatedInThisProcess,
          evseId: evseId,
          evseSerialNumber: '',
          evseFloorLevel: [],
          chargingConnectors: [],
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        isSubmitting={isUpdateEVSEMetadataPending}
        submitButtonText={'新增'}
        onEvseCreated={handleEvseCreated}
      />
    </Modal>
  );
}
